import {
  AdvancedOptions,
  AgeRange,
  ConstraintLevel,
  Ethnicity,
  OptimizationSettings,
  ReportKind,
  SmokingHabits,
} from './types';

export const REPORT_KINDS: ReadonlyArray<{
  kind: Exclude<ReportKind, 'update_products'>;
  title: string;
  description: string;
}> = [
  {
    kind: 'market_survey',
    title: 'Market Survey',
    description:
      'Profile a competitive set of products against a target demographic.',
  },
  {
    kind: 'optimization',
    title: 'Optimization',
    description:
      'Optimize a product from an existing report towards a higher predicted PQ.',
  },
  {
    kind: 'mix_and_match',
    title: 'Mix & Match',
    description:
      'Combine versions from previous optimization runs of the same parent report.',
  },
];

export const REPORT_KIND_LABELS: Record<ReportKind, string> = {
  market_survey: 'Market Survey',
  optimization: 'Optimization',
  mix_and_match: 'Mix & Match',
  update_products: 'Product Update',
};

/** Ordered from most to least constrained, the order conch expects. */
export const CONSTRAINT_LEVELS: ReadonlyArray<{
  level: ConstraintLevel;
  label: string;
}> = [
  { level: 'UHC', label: 'Ultra High' },
  { level: 'VHC', label: 'Very High' },
  { level: 'HC', label: 'High' },
  { level: 'MC', label: 'Medium' },
  { level: 'LC', label: 'Low' },
  { level: 'VLC', label: 'Very Low' },
  { level: 'ULC', label: 'Ultra Low' },
  { level: 'NC', label: 'Unconstrained' },
];

export const SMOKING_HABITS: ReadonlyArray<SmokingHabits> = [
  'All',
  'Non-Smokers',
  'Smokers',
];

export const ETHNICITIES: ReadonlyArray<{ key: Ethnicity; label: string }> = [
  { key: 'asian', label: 'Asian' },
  { key: 'black', label: 'Black' },
  { key: 'hispanic', label: 'Hispanic' },
  { key: 'white', label: 'White' },
];

export const AGE_BOUNDS = { min: 14, max: 99 };

export const DEFAULT_AGE_RANGE: AgeRange = { min: 18, max: 44, fraction: 1 };

export const MAX_AGE_RANGES = 5;
export const PROJECT_NAME_MAX_LENGTH = 128;
export const PROJECT_NAME_PATTERN = '^[A-Za-z0-9][A-Za-z0-9_\\- ]*$';
export const MIN_PRODUCT_REVIEWS = 5;
export const GGVAR_SCALE = { min: 0, max: 5 };
export const TUNING_RANGE_PERCENT = { min: 0, max: 25 };
/** Starting min/max (%) for a newly added tuned reference flavor. */
export const DEFAULT_TUNING_RANGE_PERCENT: [number, number] = [2, 8];
export const USA = 'usa';

export const DEFAULT_ADVANCED_OPTIONS: AdvancedOptions = {
  include_texture: true,
  texture_data_only: false,
  texture_only: false,
  showConfidence: true,
  show_TC_se: true,
  use_hyperlocal_glp: false,
  combine_artificial_sweeteners: false,
  wipe_out_imputed_ggvars: false,
  bootstrap_pq: false,
  plotNorm: false,
  generate_ppt: false,
  profilingEnabled: false,
  includeNoveltyScores: false,
  experimental_features: true,
  only_include_parents: false,
  include_archetypes: false,
};

export const DEFAULT_OPTIMIZATION: OptimizationSettings = {
  targetProduct: null,
  ggvarConstraints: [],
  refFlavorsToLock: [],
  refFlavorsToRemove: [],
  refFlavorsToTune: [],
  constraintLevels: ['HC', 'MC', 'LC'],
  newReferenceFlavors: ['FALSE'],
  optimizeFlavor: true,
  optimizeTexture: false,
  showNorms: false,
  tuningPlots: true,
  plotNorm: false,
  pixelList: '',
};

/**
 * Workspaces whose products are allowed into a report by default:
 * anything AFS/CLT-owned plus the workspace the report is for.
 */
export const isDefaultAllowedWorkspace = (
  workspace: string,
  targetWorkspace: string | undefined,
): boolean =>
  workspace.includes('AFS') ||
  workspace.includes('CLT') ||
  workspace === targetWorkspace;
